/**
 * The single point of contact with the admin API. Every backend call in this app goes through a
 * function exported here — no other module calls `fetch` directly (enforced by
 * `eslint.config.js`'s `no-restricted-globals` rule for `fetch`).
 *
 * Centralising this gives us one place that: builds the URL from `VITE_API_BASE_URL`, attaches the
 * bearer token, and reacts to 401/403 consistently, instead of that logic being duplicated (or
 * subtly diverging) at 27 call sites the way the prior implementation had it.
 */

import { clearToken, getToken } from "./authToken";
import { API_BASE_URL } from "./baseUrl";
import { getSsoToken } from "./sso";
import type {
  AddAdminRequest,
  Admin,
  ApprovedScope,
  AuditEntry,
  Branding,
  BootstrapRequest,
  SuperAdminRequest,
  SuperAdminSession,
  CoverageReport,
  DomainCandidate,
  FeedbackWidget,
  Features,
  IndexSchedule,
  RunReport,
  RunStatus,
  ScheduleReport,
  ScopeCandidate,
  ScopeSelection,
  SessionInfo,
  SourceInfo,
  StartRunRequest,
  TuningRules,
} from "./types";

/**
 * Where the chat API is mounted. A second base URL, not a second client: `apiRequest` below serves
 * both, so token handling and 401 behaviour have one implementation rather than two that drift.
 */
export const CHAT_BASE_URL = import.meta.env.VITE_CHAT_BASE_URL ?? "/chat";

/**
 * Resolve the bearer token to send with the next request.
 *
 * The single auth seam: every request in the app goes through here, so how a token is obtained is
 * this function's business and nothing else's. Two sources, in priority order — a break-glass
 * session stored by `authToken.ts`, then a silently renewed SSO token from `sso.ts`.
 *
 * Exported for the one caller that cannot use `apiRequest`: the chat stream reads a response body
 * incrementally, so it holds its own `fetch` and needs the same token this resolves.
 */
export async function getAccessToken(): Promise<string | null> {
  // The break-glass session wins when one is held: it is only ever set deliberately, and a
  // stale SSO session must not silently override an operator's recovery sign-in.
  return getToken() ?? (await getSsoToken());
}

/** A callback invoked whenever any request comes back 401. Registered once, by `AuthProvider`. */
let unauthorizedHandler: (() => void) | null = null;

/** Register the single 401 handler. Call again with a new function to replace it. */
export function onUnauthorized(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

/** An API call that reached the server but was rejected, carrying the status and its `detail`. */
export class ApiError extends Error {
  readonly status: number;
  readonly detail: string;

  constructor(status: number, detail: string) {
    super(detail || `request failed with status ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }

  /** True for the "authenticated but not an administrator" case the admin routes report. */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  /** True for "the index is not open yet; retry shortly" from GET /api/admin/status. */
  get isServiceUnavailable(): boolean {
    return this.status === 503;
  }

  /** True for a 409 conflict: last-admin removal, or a run already in progress. */
  get isConflict(): boolean {
    return this.status === 409;
  }
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  body?: unknown;
  /** Skip attaching a bearer token — only /api/branding needs this. */
  anonymous?: boolean;
}

/** Call the admin API. The chat API goes through `apiRequest` with `CHAT_BASE_URL`. */
function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return apiRequest<T>(API_BASE_URL, path, options);
}

/**
 * One JSON request against one of this deployment's APIs.
 *
 * Parameterised by base URL so the admin and chat surfaces share this function rather than each
 * repeating the same token attachment, 401 handling and error shaping.
 */
export async function apiRequest<T>(
  base: string,
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (!options.anonymous) {
    const token = await getAccessToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }
  if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }

  let response: Response;
  try {
    response = await fetch(`${base}${path}`, {
      method: options.method ?? "GET",
      headers,
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    });
  } catch {
    // A network failure (offline, DNS, CORS) never reaches the server at all, so it cannot carry
    // a `detail`. Re-raise as an ApiError anyway so every call site has one error type to handle.
    throw new ApiError(0, "could not reach the server; check your connection and try again");
  }

  if (response.status === 401) {
    clearToken();
    unauthorizedHandler?.();
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  const payload = text ? safeJsonParse(text) : null;

  if (!response.ok) {
    const detail = isDetailBody(payload) ? payload.detail : "";
    throw new ApiError(response.status, detail);
  }

  return payload as T;
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    // A non-JSON error body (e.g. a proxy's HTML error page) still needs to surface as *something*
    // rather than crash the parse; the caller sees an ApiError with an empty detail instead.
    return null;
  }
}

function isDetailBody(value: unknown): value is { detail: string } {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  return typeof (value as { detail?: unknown }).detail === "string";
}

// -- public routes ----------------------------------------------------------------------------

/** GET /api/branding. No auth required — the login screen calls this before anyone signs in. */
export function fetchBranding(): Promise<Branding> {
  return request<Branding>("/api/branding", { anonymous: true });
}

/**
 * GET /api/feedback-widget. No auth required — the widget has to be available on the login screen,
 * which is where a tester who cannot sign in needs to report it.
 */
export function fetchFeedbackWidget(): Promise<FeedbackWidget> {
  return request<FeedbackWidget>("/api/feedback-widget", { anonymous: true });
}

/** GET /api/session. Requires a credential (bearer or session cookie) but not admin status. */
export function fetchSession(): Promise<SessionInfo> {
  return request<SessionInfo>("/api/session");
}

/**
 * POST /api/auth/logout. Ends the backend cookie session (the Google sign-in flow). Anonymous
 * and idempotent by design — an expired session must still be able to clear its cookie — and a
 * 404 (deployment without backend sign-in) is swallowed for the same reason: sign-out must
 * always appear to work.
 */
export async function signOutServer(): Promise<void> {
  try {
    await request<void>("/api/auth/logout", { method: "POST", anonymous: true });
  } catch {
    // Nothing to recover: either there was no cookie session, or the route does not exist here.
  }
}

/** POST /api/admin/bootstrap. Requires a token; works only while the deployment has no admins. */
export function signInSuperAdmin(body: SuperAdminRequest): Promise<SuperAdminSession> {
  // `anonymous`: this is how a caller with no credential gets one, so attaching a token would be
  // circular — and a stale one would make the request fail for the wrong reason.
  return request<SuperAdminSession>("/api/auth/super-admin", {
    method: "POST",
    body,
    anonymous: true,
  });
}

export function bootstrapAdmin(body: BootstrapRequest): Promise<Admin> {
  return request<Admin>("/api/admin/bootstrap", { method: "POST", body });
}

// -- admin-only routes --------------------------------------------------------------------------

/** GET /api/admin/admins. */
export function fetchAdmins(): Promise<Admin[]> {
  return request<Admin[]>("/api/admin/admins");
}

/** POST /api/admin/admins. */
export function addAdmin(body: AddAdminRequest): Promise<Admin[]> {
  return request<Admin[]>("/api/admin/admins", { method: "POST", body });
}

/**
 * DELETE /api/admin/admins/{user_key}.
 *
 * `user_key` looks like `tenant-id/object-id` and contains a slash, but the route is declared with
 * `{user_key:path}`, so it must be sent unencoded as the path suffix rather than percent-encoded:
 * encoding the slash would make FastAPI see two path segments and 404.
 */
export function removeAdmin(userKey: string): Promise<Admin[]> {
  return request<Admin[]>(`/api/admin/admins/${userKey}`, { method: "DELETE" });
}

/** GET /api/admin/audit?limit=. */
export function fetchAuditLog(limit: number): Promise<AuditEntry[]> {
  return request<AuditEntry[]>(`/api/admin/audit?limit=${encodeURIComponent(limit)}`);
}

/** GET /api/admin/rules. */
export function fetchRules(): Promise<TuningRules> {
  return request<TuningRules>("/api/admin/rules");
}

/** PUT /api/admin/rules. */
export function saveRules(body: TuningRules): Promise<TuningRules> {
  return request<TuningRules>("/api/admin/rules", { method: "PUT", body });
}

/** GET /api/admin/domains. */
export function fetchDomains(): Promise<DomainCandidate[]> {
  return request<DomainCandidate[]>("/api/admin/domains");
}

/**
 * GET /api/admin/sources. Reports only whether each source's credentials are present, never their
 * values — a source with `configured: false` cannot run until an operator sets them.
 */
export function fetchSources(): Promise<SourceInfo[]> {
  return request<SourceInfo[]>("/api/admin/sources");
}

/** GET /api/admin/features. Which optional capabilities this deployment has switched on. */
export function fetchFeatures(): Promise<Features> {
  return request<Features>("/api/admin/features");
}

/**
 * GET /api/admin/sources/{source}/scopes. Which sites or shared drives may be indexed.
 *
 * Performs live discovery server-side, so it is the slowest call in this module — show a
 * spinner. It never fails for a provider outage: check `discovery_error` on the response, which
 * means the list holds only the already-approved rows.
 */
export function fetchScopes(sourceName: string): Promise<ScopeSelection> {
  return request<ScopeSelection>(`/api/admin/sources/${encodeURIComponent(sourceName)}/scopes`);
}

/**
 * PUT /api/admin/sources/{source}/scopes. Replaces the approved set — anything omitted becomes
 * unapproved, and its already-indexed files are removed on the next run.
 */
export function saveScopes(
  sourceName: string,
  approved: ScopeCandidate[],
): Promise<ApprovedScope[]> {
  return request<ApprovedScope[]>(`/api/admin/sources/${encodeURIComponent(sourceName)}/scopes`, {
    method: "PUT",
    body: { approved },
  });
}

/** GET /api/admin/runs. The last known run, plus a tail of its log. */
export function fetchRunStatus(): Promise<RunReport> {
  return request<RunReport>("/api/admin/runs");
}

/**
 * POST /api/admin/runs. Starts an indexing run as a detached, long-lived process.
 *
 * Raises `ApiError` with `status === 409` when a run is already in progress (refused, not
 * queued — two concurrent sweeps would fight over the same cache directory) and `status === 422`
 * for an invalid `mode`.
 */
export function startRun(body: StartRunRequest): Promise<RunStatus> {
  return request<RunStatus>("/api/admin/runs", { method: "POST", body });
}

/** GET /api/admin/schedule. When the indexer runs by itself, and when it next will. */
export function fetchSchedule(): Promise<ScheduleReport> {
  return request<ScheduleReport>("/api/admin/schedule");
}

/**
 * PUT /api/admin/schedule. Replaces the schedule; the running scheduler picks it up within a
 * minute, and saving never starts a run itself.
 *
 * Raises `ApiError` with `status === 422` for an enabled schedule with no day or no hour, and for
 * a `timezone` that is not an IANA name.
 */
export function saveSchedule(body: IndexSchedule): Promise<ScheduleReport> {
  return request<ScheduleReport>("/api/admin/schedule", { method: "PUT", body });
}

/**
 * GET /api/admin/status.
 *
 * Raises `ApiError` with `status === 503` while the index is still opening — callers must render
 * that as an informational "not ready yet, retry" state, not as "nothing is indexed". Conflating
 * "we haven't looked" with "we looked and found nothing" is the one mistake this product's design
 * treats as a hard bug.
 */
export function fetchStatus(): Promise<CoverageReport> {
  return request<CoverageReport>("/api/admin/status");
}
